/**
 * Historiography Utilities
 * Tracks progress through historical eras and unlocks content
 */

import type { AnsweredQuestionRef } from '../types/game';

export interface EraProgress {
  id: string;
  name: string;
  nameLt: string;
  nameBe: string;
  dates: string;
  icon: string;
  progress: number;
  locked: boolean;
  questionsAnswered: number;
  questionsCorrect: number;
}

export const ERAS: Omit<EraProgress, 'progress' | 'locked' | 'questionsAnswered' | 'questionsCorrect'>[] = [
  {
    id: 'kievan-rus',
    name: "Kievan Rus' & Early Principalities",
    nameLt: "Kijevo Rus' ir ankstyvosios kunigaikštystės",
    nameBe: "Кіеўская Русь і раннія княствы",
    dates: '862 – 1240',
    icon: '🏰',
  },
  {
    id: 'gdl',
    name: 'Grand Duchy of Lithuania',
    nameLt: 'Lietuvos Didžioji Kunigaikštystė',
    nameBe: 'Вялікае Княства Літоўскае',
    dates: '1236 – 1569',
    icon: '⚔️',
  },
  {
    id: 'commonwealth',
    name: 'Polish-Lithuanian Commonwealth',
    nameLt: 'Abiejų Tautų Respublika',
    nameBe: 'Рэч Паспалітая',
    dates: '1569 – 1795',
    icon: '👑',
  },
  {
    id: 'imperial',
    name: 'Russian Imperial Period',
    nameLt: 'Rusijos imperijos laikotarpis',
    nameBe: 'Перыяд Расійскай імперыі',
    dates: '1795 – 1918',
    icon: '🦅',
  },
  {
    id: 'interwar',
    name: 'Interwar Independence',
    nameLt: 'Tarpukario nepriklausomybė',
    nameBe: 'Міжваенная незалежнасць',
    dates: '1918 – 1940',
    icon: '🕊️',
  },
  {
    id: 'soviet',
    name: 'Soviet Period',
    nameLt: 'Sovietinis laikotarpis',
    nameBe: 'Савецкі перыяд',
    dates: '1940 – 1991',
    icon: '⭐',
  },
  {
    id: 'modern',
    name: 'Independence & Modern Scholarship',
    nameLt: 'Nepriklausomybė ir modernusis mokslas',
    nameBe: 'Незалежнасць і сучасная навука',
    dates: '1991 – Present',
    icon: '🌟',
  },
];

/**
 * Calculate era progress based on answered questions
 */
export function calculateEraProgress(
  answeredQuestions: AnsweredQuestionRef[]
): EraProgress[] {
  const eraMap = new Map<string, { answered: number; correct: number }>();

  // Initialize all eras
  for (const era of ERAS) {
    eraMap.set(era.id, { answered: 0, correct: 0 });
  }

  // Count answers by era
  for (const q of answeredQuestions) {
    const eraId = getEraId(q.era);
    if (eraId) {
      const current = eraMap.get(eraId) || { answered: 0, correct: 0 };
      current.answered++;
      if (q.wasCorrect) current.correct++;
      eraMap.set(eraId, current);
    }
  }

  // Build progress array
  const results: EraProgress[] = [];
  
  for (let index = 0; index < ERAS.length; index++) {
    const era = ERAS[index];
    const stats = eraMap.get(era.id) || { answered: 0, correct: 0 };
    
    // Calculate progress percentage (max 100%)
    // Assume 3 questions per era = 100%
    const progress = Math.min(100, Math.round((stats.answered / 3) * 100));
    
    // Lock era if previous era not completed (use already computed results)
    const locked = index > 0 && results[index - 1].progress < 50;

    results.push({
      ...era,
      progress,
      locked,
      questionsAnswered: stats.answered,
      questionsCorrect: stats.correct,
    });
  }
  
  return results;
}

/**
 * Map era name to era ID
 */
function getEraId(eraName: string): string | null {
  const eraMap: Record<string, string> = {
    'Kievan Rus': 'kievan-rus',
    'Early Medieval': 'kievan-rus',
    'GDL': 'gdl',
    'Polish-Lithuanian Commonwealth': 'commonwealth',
    'Russian Imperial': 'imperial',
    '19th Century': 'imperial',
    'Interwar': 'interwar',
    'Soviet': 'soviet',
    'Modern Historiography': 'modern',
  };

  return eraMap[eraName] || null;
}

/**
 * Get localized era name
 */
export function getLocalizedEraName(
  era: EraProgress,
  language: 'en' | 'lt' | 'be'
): string {
  switch (language) {
    case 'lt':
      return era.nameLt;
    case 'be':
      return era.nameBe;
    default:
      return era.name;
  }
}

/**
 * Get total progress across all eras
 */
export function getTotalProgress(eras: EraProgress[]): number {
  if (eras.length === 0) return 0;
  const totalProgress = eras.reduce((sum, era) => sum + era.progress, 0);
  return Math.round(totalProgress / eras.length);
}

/**
 * Get next unlockable era
 */
export function getNextUnlockableEra(eras: EraProgress[]): EraProgress | null {
  return eras.find(era => era.locked && era.progress > 0) || null;
}

/**
 * Get completed eras count
 */
export function getCompletedErasCount(eras: EraProgress[]): number {
  return eras.filter(era => era.progress >= 100).length;
}

/**
 * Maps a question era (as stored on Question.era) to a representative
 * symbol/emoji shown on the QuestionScreen. The mapping is intentionally
 * a superset of the era IDs above because the Question.era values are
 * narrative labels ("Kievan Rus", "19th Century", "Soviet", …) while
 * ERAS uses normalised ids.
 */
const ERA_SYMBOLS: Record<string, { icon: string; dates: string }> = {
  'Kievan Rus': { icon: '🏰', dates: '862–1240' },
  'Early Medieval': { icon: '🛡️', dates: '900–1240' },
  'GDL': { icon: '⚔️', dates: '1236–1569' },
  'Polish-Lithuanian Commonwealth': { icon: '👑', dates: '1569–1795' },
  'Russian Imperial': { icon: '🦅', dates: '1795–1917' },
  '19th Century': { icon: '📜', dates: '1800–1900' },
  'Interwar': { icon: '🕊️', dates: '1918–1940' },
  'Soviet': { icon: '⭐', dates: '1940–1991' },
  'Modern Historiography': { icon: '🌟', dates: '1991–present' },
};

/** Get the symbol + dates label for a question era */
export function getEraSymbol(era: string): { icon: string; dates: string } {
  return ERA_SYMBOLS[era] ?? { icon: '📖', dates: '' };
}

/** Localized era name for a Question.era string (vs EraProgress) */
export function getLocalizedQuestionEraName(
  era: string,
  language: 'en' | 'lt' | 'be'
): string {
  const id = (function (): string | null {
    const map: Record<string, string> = {
      'Kievan Rus': 'kievan-rus',
      'Early Medieval': 'kievan-rus',
      'GDL': 'gdl',
      'Polish-Lithuanian Commonwealth': 'commonwealth',
      'Russian Imperial': 'imperial',
      '19th Century': 'imperial',
      'Interwar': 'interwar',
      'Soviet': 'soviet',
      'Modern Historiography': 'modern',
    };
    return map[era] || null;
  })();

  const found = id ? ERAS.find(e => e.id === id) : undefined;
  if (!found) return era;

  switch (language) {
    case 'lt':
      return found.nameLt;
    case 'be':
      return found.nameBe;
    default:
      return found.name;
  }
}
